/**
 * W3C Trace Context (`traceparent`) generation + the propagation allow-check.
 *
 * Header format: `00-<32 hex trace-id>-<16 hex parent-id>-<2 hex flags>`.
 * Ids come from `crypto.getRandomValues` (via {@link randomHex}) and are never
 * all-zero (the spec reserves that value as invalid).
 */

import { randomHex } from "./session";

export interface TraceContext {
  traceId: string;
  spanId: string;
  sampled: boolean;
  traceparent: string;
}

const TRACEPARENT_RE = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/;
const ZERO_32 = "0".repeat(32);
const ZERO_16 = "0".repeat(16);

function nonZeroHex(bytes: number, zero: string): string {
  let value = randomHex(bytes);
  while (value === zero) value = randomHex(bytes);
  return value;
}

/** Generate a fresh trace context. */
export function newTraceContext(sampled = true): TraceContext {
  const traceId = nonZeroHex(16, ZERO_32);
  const spanId = nonZeroHex(8, ZERO_16);
  return { traceId, spanId, sampled, traceparent: `00-${traceId}-${spanId}-${sampled ? "01" : "00"}` };
}

/** Generate a `traceparent` header value. */
export function traceparent(sampled = true): string {
  return newTraceContext(sampled).traceparent;
}

/** Parse a `traceparent` header value; `null` if malformed or all-zero ids. */
export function parseTraceparent(value: string | null | undefined): Omit<TraceContext, "traceparent"> | null {
  if (typeof value !== "string") return null;
  const m = TRACEPARENT_RE.exec(value.trim().toLowerCase());
  if (!m) return null;
  const [, traceId, spanId, flags] = m as unknown as [string, string, string, string];
  if (traceId === ZERO_32 || spanId === ZERO_16) return null;
  return { traceId, spanId, sampled: (Number.parseInt(flags, 16) & 1) === 1 };
}

export interface TracePropagationOptions {
  /**
   * Absolute `scheme://host` origins (or bare hosts) that are Plerity's own API
   * hosts, e.g. `["https://crm-api.animasmarketing.com"]`. Same-origin and
   * relative URLs are always allowed.
   */
  allowedHosts?: readonly string[];
  /** The page origin; defaults to `location.origin` when a DOM is present. */
  origin?: string;
}

function hostOf(value: string): string | null {
  try {
    return new URL(value).host.toLowerCase();
  } catch {
    return null;
  }
}

function currentOrigin(explicit?: string): string | null {
  if (explicit) return explicit;
  if (typeof location !== "undefined" && typeof location.origin === "string" && location.origin !== "null") {
    return location.origin;
  }
  return null;
}

/**
 * Should trace headers (`traceparent`, `X-Request-ID`) be attached to a request?
 *
 * TRUE for relative URLs, same-origin URLs, and configured API hosts. FALSE for
 * every third party — never leak internal correlation ids to someone else's
 * server (and never trigger a CORS preflight against one).
 *
 * @param requestUrl  the request URL (absolute or relative)
 * @param baseURL     the client's base URL, used when `requestUrl` is relative
 */
export function shouldPropagateTrace(
  requestUrl: string | undefined,
  baseURL: string | undefined,
  options: TracePropagationOptions = {}
): boolean {
  const isAbsolute = (u: string | undefined): u is string =>
    typeof u === "string" && /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(u);
  const target = isAbsolute(requestUrl) ? requestUrl : isAbsolute(baseURL) ? baseURL : null;
  if (target === null) return true; // relative everywhere → same-origin
  const normalized = target.startsWith("//") ? `https:${target}` : target;
  const host = hostOf(normalized);
  if (host === null) return false;
  const origin = currentOrigin(options.origin);
  if (origin && hostOf(origin) === host) return true;
  return (options.allowedHosts ?? []).some((allowed) => {
    const allowedHost = hostOf(allowed.includes("://") ? allowed : `https://${allowed}`);
    return allowedHost === host;
  });
}
